import { EditOutlined } from '@ant-design/icons';
import { ProCard, ProDescriptions } from '@ant-design/pro-components';
import { history } from '@umijs/max';
import {
  Alert,
  Avatar,
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  message,
  Skeleton,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import React, {
  type CSSProperties,
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CrmList } from '@/components/CrmList';
import QuietEmpty from '@/components/QuietEmpty';
import Empty from '@/components/QuietEmpty';
import {
  ActivityPanel,
  ActivityPanelFollowUps,
} from '@/modules/zy-crm/components/activity-panel/export';
import { AiDetailPanel } from '@/modules/zy-crm/components/ai-detail-panel';
import { AttachmentPanel } from '@/modules/zy-crm/components/attachment-panel';
import { BusinessModal } from '@/modules/zy-crm/components/business-modal';
import { BusinessTripRelationPanel } from '@/modules/zy-crm/components/business-trip-relation-panel';
import {
  type CalendarEventFormValues,
  calendarEventFormDefaultValues,
  createCalendarRelationFormValue,
  normalizeCalendarRelationPayload,
} from '@/modules/zy-crm/components/calendar-event-form';
import { ContactEditModal } from '@/modules/zy-crm/components/contact-edit-modal';
import { ContactOpportunityCreateModal } from '@/modules/zy-crm/components/contact-opportunity-create-modal';
import { EntityTagEditor } from '@/modules/zy-crm/components/crm-tags';
import { CrmTaskRelationPanel } from '@/modules/zy-crm/components/crm-task-relation-panel';
import { CustomerReferenceLink } from '@/modules/zy-crm/components/customer-detail-drawer';
import { DetailNavigation } from '@/modules/zy-crm/components/detail-navigation';
import { DetailOperationLogSection } from '@/modules/zy-crm/components/detail-operation-log-section';
import { DetailSurface } from '@/modules/zy-crm/components/detail-surface';
import { OpportunityReferenceLink } from '@/modules/zy-crm/components/opportunity-detail-drawer';
import { OpportunityEditModal } from '@/modules/zy-crm/components/opportunity-edit-modal';
import {
  type ReferralBlockEditorConfig,
  ReferralSection as UnifiedReferralSection,
} from '@/modules/zy-crm/components/referral-section';
import { TeamMembersPanel } from '@/modules/zy-crm/components/team-members-panel';
import { useMemberDirectory } from '@/modules/zy-crm/member-directory';
import type { RelationCreateContext } from '@/modules/zy-crm/types/relation-create-context';
import {
  type CrmScopedViewMode,
  resolveCrmScopedViewModeFromPathname,
} from '@/modules/zy-crm/utils/crm-view';
import {
  defaultDetailSection,
  detailSectionLabel,
  normalizeDetailSections,
} from '@/modules/zy-crm/utils/detail-navigation';
import {
  formatCrmDate,
  resolveRelationText,
  toApiDateTime,
} from '@/modules/zy-crm/utils/display';
import {
  fetchAreaOptions,
  fetchContactOptions,
  fetchCustomerOptions,
  fetchLeadOptions,
  fetchOpportunityOptions,
  type RelationOption,
  resolveRelationLabel,
} from '@/modules/zy-crm/utils/relation-options';
import { getAttachmentPreviewUrl } from '@/services/bff/attachment.api';
import {
  createCalendarEvent,
  updateCalendarEvent,
} from '@/services/bff/calendar.api';
import { queryContactDrawerProfile } from '@/services/bff/contact.api';
import { createReferral } from '@/services/bff/referral.api';
import type { CalendarEventItem } from '@/services/bff/types/calendar';
import type {
  ContactDrawerOpportunityItem,
  ContactDrawerProfile,
  ContactItem,
} from '@/services/bff/types/contact';
import type { ReferralItem } from '@/services/bff/types/referral';
import { createAppRequest } from '@/services/request';
import styles from './style.module.less';

const { Text, Title } = Typography;
const appRequest = createAppRequest();

type ContactSectionKey =
  | 'details'
  | 'activities'
  | 'followUps'
  | 'referrals'
  | 'teamMembers'
  | 'opportunities'
  | 'tasks'
  | 'businessTrips'
  | 'attachments'
  | 'operationLogs';

type RelatedEditorState = {
  kind: 'opportunity';
  record: ContactDrawerOpportunityItem;
};

type ContactActivityScheduleModalState = {
  mode: 'create' | 'edit';
  item?: CalendarEventItem;
};

type ContactDrawerOpenInput = {
  contactUuid?: string;
  contactName?: ReactNode;
};

type ContactDetailDrawerContextValue = {
  openContact: (input: ContactDrawerOpenInput) => void;
};

type ContactDetailDrawerProviderProps = {
  children: ReactNode;
};

type ContactReferenceLinkProps = {
  contactUuid?: string;
  contactName?: ReactNode;
  fallbackName?: ReactNode;
  emptyText?: ReactNode;
  canViewDetail?: boolean;
  style?: CSSProperties;
};

const ContactDetailDrawerContext =
  createContext<ContactDetailDrawerContextValue | null>(null);

const EMPTY_PAGE = {
  list: [],
  total: 0,
};

const SECTIONS: Array<{ key: ContactSectionKey; label: string }> = [
  { key: 'details', label: '详细信息' },
  { key: 'activities', label: '活动' },
  { key: 'followUps', label: '跟进记录' },
  { key: 'referrals', label: '介绍关系' },
  { key: 'teamMembers', label: '协作成员' },
  { key: 'opportunities', label: '商机' },
  { key: 'tasks', label: '任务' },
  { key: 'businessTrips', label: '相关出差' },
  { key: 'attachments', label: '附件' },
  { key: 'operationLogs', label: '操作记录' },
];

const descColumns = { xs: 1, sm: 2 } as const;

const normalizeText = (value: unknown) =>
  typeof value === 'string' ? value.trim() : '';

const normalizeOptionalText = (value: unknown) =>
  normalizeText(value) || undefined;

const toPickerValue = (value?: string | null) =>
  value ? dayjs(value) : undefined;

const toIsoValue = (value?: Dayjs | null) => toApiDateTime(value);

const normalizeDrawerSections = (
  sections?: ContactDrawerProfile['sections'],
) => {
  const allowedKeys = new Set<ContactSectionKey>(
    SECTIONS.map((section) => section.key),
  );
  const sectionMap = new Map<
    ContactSectionKey,
    { key: ContactSectionKey; label: string; count?: number }
  >();
  const normalized = (sections || [])
    .filter((section): section is ContactDrawerProfile['sections'][number] =>
      allowedKeys.has(section.key as ContactSectionKey),
    )
    .map((section) => ({
      ...section,
      key: section.key as ContactSectionKey,
    }));

  for (const section of normalized) {
    sectionMap.set(section.key, section);
  }

  for (const section of SECTIONS) {
    if (!sectionMap.has(section.key)) {
      sectionMap.set(section.key, { ...section, count: 0 });
    }
  }

  return normalizeDetailSections(
    'contact',
    SECTIONS.map((section) => sectionMap.get(section.key) || section),
  );
};

const relatedEditorTitleMap: Record<RelatedEditorState['kind'], string> = {
  opportunity: '编辑商机',
};

export function useContactDetailDrawer() {
  const ctx = useContext(ContactDetailDrawerContext);
  return (
    ctx || {
      openContact: () => {},
    }
  );
}

export function ContactDetailDrawerProvider({
  children,
}: ContactDetailDrawerProviderProps) {
  const [open, setOpen] = useState(false);
  const [contactUuid, setContactUuid] = useState<string>();
  const [fallbackName, setFallbackName] = useState<ReactNode>();
  const [profile, setProfile] = useState<ContactDrawerProfile>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error>();
  const latestProfileRequestRef = useRef(0);
  const pathname =
    history.location?.pathname ||
    (typeof window !== 'undefined' ? window.location.pathname : '');
  const pageViewMode = resolveCrmScopedViewModeFromPathname(
    pathname + (history.location?.search || ''),
    'contacts',
  );

  const openContact = useCallback((input: ContactDrawerOpenInput) => {
    const uuid = normalizeText(input.contactUuid);
    if (!uuid) {
      message.warning('该记录缺少联系人 UUID，无法打开联系人详情');
      return;
    }
    setProfile(undefined);
    setError(undefined);
    setContactUuid(uuid);
    setFallbackName(input.contactName);
    setOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setOpen(false);
  }, []);

  const loadContact = useCallback(async () => {
    if (!contactUuid || !open) return;
    const requestId = latestProfileRequestRef.current + 1;
    latestProfileRequestRef.current = requestId;
    setLoading(true);
    setError(undefined);
    try {
      const data = await queryContactDrawerProfile(contactUuid, {
        viewMode: pageViewMode,
      });
      if (requestId !== latestProfileRequestRef.current) {
        return;
      }
      setProfile(data);
    } catch (e) {
      if (requestId !== latestProfileRequestRef.current) {
        return;
      }
      const err = e instanceof Error ? e : new Error('联系人详情加载失败');
      setError(err);
      setProfile(undefined);
      message.error(err.message);
    } finally {
      if (requestId === latestProfileRequestRef.current) {
        setLoading(false);
      }
    }
  }, [contactUuid, open, pageViewMode]);

  useEffect(() => {
    void loadContact();
  }, [loadContact]);

  const value = useMemo(() => ({ openContact }), [openContact]);

  return (
    <ContactDetailDrawerContext.Provider value={value}>
      {children}
      <ContactDetailDrawerView
        open={open}
        loading={loading}
        error={error}
        profile={profile}
        fallbackName={fallbackName}
        pageViewMode={pageViewMode}
        onClose={closeDrawer}
        onRefresh={() => void loadContact()}
      />
    </ContactDetailDrawerContext.Provider>
  );
}

export function ContactReferenceLink({
  contactUuid,
  contactName,
  fallbackName,
  emptyText = '-',
  canViewDetail = true,
  style,
}: ContactReferenceLinkProps) {
  const ctx = useContext(ContactDetailDrawerContext);

  if (!ctx) {
    return (
      <ContactDetailDrawerProvider>
        <ContactReferenceLink
          contactUuid={contactUuid}
          contactName={contactName}
          fallbackName={fallbackName}
          emptyText={emptyText}
          canViewDetail={canViewDetail}
          style={style}
        />
      </ContactDetailDrawerProvider>
    );
  }

  return (
    <ContactReferenceLinkContent
      contactUuid={contactUuid}
      contactName={contactName}
      fallbackName={fallbackName}
      emptyText={emptyText}
      canViewDetail={canViewDetail}
      style={style}
      openContact={ctx.openContact}
    />
  );
}

function ContactReferenceLinkContent({
  contactUuid,
  contactName,
  fallbackName,
  emptyText = '-',
  canViewDetail = true,
  style,
  openContact,
}: ContactReferenceLinkProps & {
  openContact: ContactDetailDrawerContextValue['openContact'];
}) {
  const label = contactName || fallbackName || emptyText;
  if (!normalizeText(contactUuid) || canViewDetail === false) {
    const content = (
      <Text type="secondary" style={style}>
        {label}
      </Text>
    );
    if (normalizeText(contactUuid) && canViewDetail === false) {
      return <Tooltip title="无详情权限">{content}</Tooltip>;
    }
    return content;
  }
  return (
    <Button
      type="link"
      className={styles.contactLink}
      style={style}
      onClick={() => openContact({ contactUuid, contactName: label })}
    >
      {label}
    </Button>
  );
}

function renderReferralObject(
  type: ReferralItem['referrerType'] | ReferralItem['targetType'],
  uuid?: string,
  name?: string,
) {
  const label = name || uuid || '-';
  if (!uuid) {
    return <Text type="secondary">{label}</Text>;
  }
  switch (type) {
    case 'contact':
      return <ContactReferenceLink contactUuid={uuid} contactName={label} />;
    case 'customer':
      return (
        <Button
          type="link"
          style={{ padding: 0 }}
          onClick={() => history.push(`/zy-crm/customer/${uuid}`)}
        >
          {label}
        </Button>
      );
    case 'opportunity':
      return (
        <OpportunityReferenceLink
          opportunityUuid={uuid}
          opportunityName={label}
        />
      );
    default:
      return <Text>{label}</Text>;
  }
}

type ContactReferralValues = {
  objectType?: ReferralItem['targetType'];
  objectUuid?: string;
  referralTime?: Dayjs;
  isPrimary?: boolean;
  profitShareRatio?: number;
  remark?: string;
};

function ReferralSection({
  contactUuid,
  onRefresh,
  incoming,
  outgoing,
}: {
  contactUuid: string;
  onRefresh: () => void;
  incoming: ContactDrawerProfile['related']['referralsIn'];
  outgoing: ContactDrawerProfile['related']['referralsOut'];
}) {
  const [incomingForm] = Form.useForm<ContactReferralValues>();
  const [outgoingForm] = Form.useForm<ContactReferralValues>();
  const [options, setOptions] = useState<{
    incoming: RelationOption[];
    outgoing: RelationOption[];
  }>({ incoming: [], outgoing: [] });
  const searchVersions = useRef({ incoming: 0, outgoing: 0 });
  const loaders = {
    contact: fetchContactOptions,
    customer: fetchCustomerOptions,
    lead: fetchLeadOptions,
    opportunity: fetchOpportunityOptions,
  };
  const labels = {
    contact: '联系人',
    customer: '客户',
    lead: '线索',
    opportunity: '商机',
  };
  const buildEditor = (
    direction: 'incoming' | 'outgoing',
  ): ReferralBlockEditorConfig<ContactReferralValues, ReferralItem> => {
    const form = direction === 'incoming' ? incomingForm : outgoingForm;
    const isIncoming = direction === 'incoming';
    const loadOptions = async (keyword?: string) => {
      const version = ++searchVersions.current[direction];
      const type = form.getFieldValue(
        'objectType',
      ) as ContactReferralValues['objectType'];
      if (!type) return;
      try {
        const result = await loaders[type](keyword);
        if (version !== searchVersions.current[direction]) return;
        setOptions((previous) => ({
          ...previous,
          [direction]: result.filter(
            (item) => type !== 'contact' || item.value !== contactUuid,
          ),
        }));
      } catch {
        if (version !== searchVersions.current[direction]) return;
        setOptions((previous) => ({ ...previous, [direction]: [] }));
        message.error('加载介绍关系对象失败，请重试');
      }
    };
    return {
      form,
      createOnly: true,
      responsive: true,
      createTitle: isIncoming ? '新增介绍人' : '新增介绍对象',
      editTitle: '介绍关系',
      buildInitialValues: () => ({ isPrimary: false }),
      onOpen: () => {
        ++searchVersions.current[direction];
        setOptions((previous) => ({ ...previous, [direction]: [] }));
      },
      onClose: () => {
        ++searchVersions.current[direction];
      },
      fields: [
        {
          name: 'objectType',
          type: 'radio',
          label: isIncoming ? '介绍人类型' : '目标类型',
          rules: [{ required: true, message: '请选择类型' }],
          options: (isIncoming
            ? (['customer', 'contact'] as const)
            : (['contact', 'customer', 'lead', 'opportunity'] as const)
          ).map((value) => ({ value, label: labels[value] })),
          resetNames: ['objectUuid'],
          onValueChange: () => {
            setOptions((previous) => ({ ...previous, [direction]: [] }));
            void loadOptions();
          },
        },
        {
          name: 'objectUuid',
          type: 'select',
          label: isIncoming ? '介绍人' : '介绍对象',
          rules: [
            {
              required: true,
              message: isIncoming ? '请选择介绍人' : '请选择介绍对象',
            },
            {
              validator: async (_, value) => {
                if (
                  form.getFieldValue('objectType') === 'contact' &&
                  value === contactUuid
                )
                  throw new Error('介绍人不能与被介绍对象相同');
              },
            },
          ],
          options: options[direction],
          disabled: () => !form.getFieldValue('objectType'),
          placeholder: '请选择类型后搜索选择',
          onLoadOptions: loadOptions,
        },
        {
          name: 'referralTime',
          type: 'date',
          label: '介绍时间',
          showTime: true,
        },
        { name: 'isPrimary', type: 'switch', label: '是否主介绍人' },
        {
          name: 'profitShareRatio',
          type: 'number',
          label: '分润比例',
          min: 0,
          max: 100,
          precision: 2,
          addonAfter: '%',
          rules: [
            {
              type: 'number',
              min: 0,
              max: 100,
              message: '分润比例必须在 0 到 100 之间',
            },
          ],
        },
        {
          name: 'remark',
          type: 'textarea',
          label: '备注',
          rows: 3,
          maxLength: 500,
          showCount: true,
          rules: [{ max: 500, message: '备注最多 500 字' }],
        },
      ],
      onSubmit: async ({ values }) => {
        try {
          await createReferral({
            referrerType: isIncoming
              ? (values.objectType as 'contact' | 'customer')
              : 'contact',
            referrerUuid: isIncoming ? values.objectUuid! : contactUuid,
            targetType: isIncoming ? 'contact' : values.objectType!,
            targetUuid: isIncoming ? contactUuid : values.objectUuid!,
            referralTime: toIsoValue(values.referralTime),
            isPrimary: values.isPrimary ? 1 : 0,
            profitShareRatio: values.profitShareRatio ?? undefined,
            remark: normalizeOptionalText(values.remark),
          });
        } catch (error) {
          message.error(
            error instanceof Error
              ? error.message
              : '介绍关系新增失败，请稍后重试',
          );
          throw error;
        }
        message.success('介绍关系新增成功');
        onRefresh();
      },
    };
  };
  return (
    <UnifiedReferralSection
      title={detailSectionLabel('referrals')}
      hint={`传入 ${incoming.total} 条，传出 ${outgoing.total} 条`}
      renderSection={({ title, hint, children }) => (
        <ProCard
          title={title}
          subTitle={
            hint ? (
              <span className={styles.cardSubTitle}>{hint}</span>
            ) : undefined
          }
          className={styles.sectionCard}
        >
          {children}
        </ProCard>
      )}
      blocks={[
        {
          key: 'incoming',
          title: '谁介绍了当前联系人',
          editor: buildEditor('incoming'),
          items: incoming.list || [],
          emptyText: '',
          columns: [
            {
              title: '介绍人类型',
              dataIndex: 'referrerType',
              key: 'referrerType',
              render: (value: ReferralItem['referrerType']) => (
                <Tag color="blue">
                  {value === 'contact' ? '联系人' : '客户'}
                </Tag>
              ),
            },
            {
              title: '介绍人',
              dataIndex: 'referrerName',
              key: 'referrerName',
              render: (_, record: ReferralItem) =>
                renderReferralObject(
                  record.referrerType,
                  record.referrerUuid,
                  record.referrerName,
                ),
            },
            {
              title: '介绍时间',
              dataIndex: 'referralTime',
              key: 'referralTime',
              render: (value?: string) => formatCrmDate(value),
            },
            {
              title: '是否主介绍人',
              dataIndex: 'isPrimary',
              key: 'isPrimary',
              render: (value?: number | boolean) => (
                <Tag color={value ? 'gold' : 'default'}>
                  {value ? '是' : '否'}
                </Tag>
              ),
            },
            {
              title: '分润比例',
              dataIndex: 'profitShareRatio',
              key: 'profitShareRatio',
              render: (value?: number | string) =>
                value === undefined || value === null || value === ''
                  ? '-'
                  : `${value}%`,
            },
            {
              title: '备注',
              dataIndex: 'remark',
              key: 'remark',
              render: (value?: string) => value || '-',
            },
          ],
        },
        {
          key: 'outgoing',
          title: '当前联系人介绍出去的对象',
          editor: buildEditor('outgoing'),
          items: outgoing.list || [],
          emptyText: '',
          columns: [
            {
              title: '目标类型',
              dataIndex: 'targetType',
              key: 'targetType',
              render: (value: ReferralItem['targetType']) => (
                <Tag color="blue">
                  {value === 'contact'
                    ? '联系人'
                    : value === 'customer'
                      ? '客户'
                      : value === 'lead'
                        ? '线索'
                        : value}
                </Tag>
              ),
            },
            {
              title: '介绍出去的对象',
              dataIndex: 'targetName',
              key: 'targetName',
              render: (_, record: ReferralItem) =>
                renderReferralObject(
                  record.targetType,
                  record.targetUuid,
                  record.targetName,
                ),
            },
            {
              title: '介绍时间',
              dataIndex: 'referralTime',
              key: 'referralTime',
              render: (value?: string) => formatCrmDate(value),
            },
            {
              title: '备注',
              dataIndex: 'remark',
              key: 'remark',
              render: (value?: string) => value || '-',
            },
          ],
        },
      ]}
    />
  );
}

export function ContactDetailDrawerView({
  open,
  loading,
  error,
  profile,
  fallbackName,
  pageViewMode,
  onClose,
  onRefresh,
}: {
  open: boolean;
  loading: boolean;
  error?: Error;
  profile?: ContactDrawerProfile;
  fallbackName?: ReactNode;
  pageViewMode: CrmScopedViewMode;
  onClose: () => void;
  onRefresh: () => void;
}) {
  const [activeSection, setActiveSection] =
    useState<ContactSectionKey>('details');
  const [_attachmentCount, setAttachmentCount] = useState<number | undefined>();
  const contact = profile?.contact;
  const [memberOptions, setMemberOptions] = useState<RelationOption[]>([]);
  const [customerOptions, setCustomerOptions] = useState<RelationOption[]>([]);
  const [_areaOptions, setAreaOptions] = useState<RelationOption[]>([]);
  const [parentOptions, setParentOptions] = useState<RelationOption[]>([]);
  const [contactEditOpen, setContactEditOpen] = useState(false);
  const [relatedEditor, setRelatedEditor] = useState<RelatedEditorState | null>(
    null,
  );
  const [relatedSubmitting, setRelatedSubmitting] = useState(false);
  const [relatedForm] = Form.useForm();
  const [scheduleForm] = Form.useForm<CalendarEventFormValues>();
  const [contactOpportunityCreateOpen, setContactOpportunityCreateOpen] =
    useState(false);
  const [opportunityCreateOpen, setOpportunityCreateOpen] = useState(false);
  const [scheduleEditor, setScheduleEditor] =
    useState<ContactActivityScheduleModalState | null>(null);
  const [_scheduleSubmitting, setScheduleSubmitting] = useState(false);
  const previousContactUuidRef = useRef<string | undefined>(undefined);
  const { memberOptions: sharedMemberOptions, searchMembers } =
    useMemberDirectory();
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    void Promise.all([
      Promise.resolve(sharedMemberOptions).then(setMemberOptions),
      fetchCustomerOptions().then(setCustomerOptions),
      fetchAreaOptions().then(setAreaOptions),
      fetchContactOptions().then(setParentOptions),
    ]);
  }, [open, sharedMemberOptions]);

  const _loadRelatedMembers = useCallback(
    async (keyword?: string) => searchMembers(keyword),
    [searchMembers],
  );

  const _openScheduleCreate = useCallback(() => {
    if (!contact?.uuid) {
      return;
    }
    scheduleForm.resetFields();
    scheduleForm.setFieldsValue(
      calendarEventFormDefaultValues({
        title: '',
        eventType: 'my_schedule',
        startTime: dayjs(),
        endTime: dayjs().add(1, 'hour'),
        allDay: false,
        ownerMemberUuid: normalizeOptionalText(contact.ownerUserUuid),
        participantMemberUuids: [],
        relations: [createCalendarRelationFormValue('contact', contact.uuid)],
      }),
    );
    setScheduleEditor({ mode: 'create' });
  }, [contact?.ownerUserUuid, contact?.uuid, scheduleForm]);

  const closeScheduleEditor = useCallback(() => {
    setScheduleEditor(null);
    setScheduleSubmitting(false);
    scheduleForm.resetFields();
  }, [scheduleForm]);

  const _submitScheduleEditor = useCallback(async () => {
    if (!contact?.uuid || !scheduleEditor) {
      return;
    }
    const values = await scheduleForm.validateFields();
    setScheduleSubmitting(true);
    try {
      const startTime = toApiDateTime(values.startTime);
      if (!startTime) {
        throw new Error('invalid start time');
      }
      const payload = {
        title: normalizeText(values.title),
        eventType: normalizeOptionalText(values.eventType) || 'my_schedule',
        startTime,
        endTime: toApiDateTime(values.endTime),
        allDay: Boolean(values.allDay),
        location: normalizeOptionalText(values.location),
        description: normalizeOptionalText(values.description),
        ownerMemberUuid: normalizeOptionalText(values.ownerMemberUuid),
        participantMemberUuids: Array.isArray(values.participantMemberUuids)
          ? (values.participantMemberUuids
              .map((item) => normalizeOptionalText(item))
              .filter(Boolean) as string[])
          : [],
        relations: normalizeCalendarRelationPayload(values.relations) || [
          {
            bizType: 'contact' as const,
            bizUuid: contact.uuid,
            relationRole: 'primary' as const,
          },
        ],
      };

      if (scheduleEditor.mode === 'edit' && scheduleEditor.item?.uuid) {
        await updateCalendarEvent(scheduleEditor.item.uuid, payload);
        message.success('日程已更新');
      } else {
        await createCalendarEvent(payload);
        message.success('日程已创建');
      }
      closeScheduleEditor();
      onRefresh();
    } catch (error) {
      console.error(error);
      message.error('保存失败，请稍后重试');
    } finally {
      setScheduleSubmitting(false);
    }
  }, [
    closeScheduleEditor,
    contact?.uuid,
    onRefresh,
    scheduleEditor,
    scheduleForm,
  ]);

  const openRelatedEditor = useCallback(
    (next: RelatedEditorState) => {
      setRelatedEditor(next);
      if (next.kind === 'opportunity') {
        relatedForm.setFieldsValue({
          ...next.record,
          dealTime: toPickerValue(next.record.dealTime),
          totalPrice: next.record.totalPrice
            ? Number(next.record.totalPrice)
            : undefined,
        });
      }
    },
    [relatedForm],
  );

  const closeRelatedEditor = useCallback(() => {
    setRelatedEditor(null);
    setRelatedSubmitting(false);
    relatedForm.resetFields();
  }, [relatedForm]);

  const submitRelatedEditor = useCallback(async () => {
    if (!relatedEditor) {
      return;
    }
    const values = await relatedForm.validateFields();
    setRelatedSubmitting(true);
    try {
      if (relatedEditor.kind === 'opportunity') {
        await appRequest(
          `/bff/zy-crm/opportunities/${relatedEditor.record.uuid}`,
          {
            method: 'PATCH',
            data: {
              name: normalizeOptionalText(values.name),
              endStatus:
                values.endStatus === undefined || values.endStatus === null
                  ? undefined
                  : Number(values.endStatus),
              dealTime: toIsoValue(values.dealTime),
              totalPrice:
                values.totalPrice === undefined || values.totalPrice === null
                  ? undefined
                  : String(values.totalPrice),
              remark: normalizeOptionalText(values.remark),
            },
          },
        );
      }
      message.success('更新成功');
      closeRelatedEditor();
      onRefresh();
    } finally {
      setRelatedSubmitting(false);
    }
  }, [closeRelatedEditor, onRefresh, relatedEditor, relatedForm]);

  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState('');

  const displayName =
    normalizeText(contact?.name) || (fallbackName as string) || '未命名联系人';
  const avatarUrl = avatarPreviewUrl;
  const customerUuid =
    normalizeOptionalText(profile?.customer?.uuid) ||
    normalizeOptionalText(contact?.customerUuid);
  const customerName =
    normalizeOptionalText(profile?.customer?.name) ||
    resolveRelationLabel(customerUuid, customerOptions, undefined);

  const sexLabel = contact?.sex === 1 ? '男' : contact?.sex === 2 ? '女' : '-';
  const relationContext = useMemo<RelationCreateContext>(
    () => ({
      customerUuid,
      customerName,
      contactUuid: contact?.uuid,
      contactName: contact?.name,
      signContactUuid: contact?.uuid,
    }),
    [contact?.name, contact?.uuid, customerName, customerUuid],
  );
  const sections = useMemo(
    () => normalizeDrawerSections(profile?.sections),
    [profile?.sections],
  );
  const displayedSection =
    sections.find((section) => section.key === activeSection)?.key ||
    defaultDetailSection('contact', sections);
  useEffect(() => {
    if (!open) {
      previousContactUuidRef.current = undefined;
      setAvatarPreviewUrl('');
      return;
    }

    const currentContactUuid = normalizeText(contact?.uuid);
    if (!currentContactUuid) {
      return;
    }

    const sectionList = sections;
    const fallbackSection =
      (defaultDetailSection('contact', sectionList) as ContactSectionKey) ||
      'details';
    const sectionKeys = new Set(
      sectionList.map((section) => section.key as ContactSectionKey),
    );
    const isContactChanged =
      previousContactUuidRef.current !== currentContactUuid;

    if (isContactChanged) {
      previousContactUuidRef.current = currentContactUuid;
      setActiveSection(fallbackSection);
      return;
    }

    if (!sectionKeys.has(activeSection)) {
      setActiveSection(fallbackSection);
    }
  }, [activeSection, contact?.uuid, open, sections]);

  useEffect(() => {
    const avatarAttachmentUuid = normalizeText(contact?.avatarAttachmentUuid);
    if (!open || !avatarAttachmentUuid) {
      setAvatarPreviewUrl('');
      return;
    }

    let disposed = false;

    const loadAvatarPreview = async () => {
      try {
        const result = await getAttachmentPreviewUrl(avatarAttachmentUuid);
        if (disposed) {
          return;
        }
        setAvatarPreviewUrl(String(result.url || '').trim());
      } catch {
        if (disposed) {
          return;
        }
        setAvatarPreviewUrl('');
      }
    };

    void loadAvatarPreview();

    return () => {
      disposed = true;
    };
  }, [contact?.avatarAttachmentUuid, open]);

  const _memberNameMap = Object.fromEntries(
    memberOptions.map((item) => [item.value, item.label]),
  );

  return (
    <BusinessModal
      open={open}
      onCancel={handleClose}
      destroyOnHidden
      title={null}
      detail
      aria-label="联系人详情"
    >
      <div className={styles.shell} data-crm-shell>
        {loading && !contact ? (
          <div className={styles.loading}>
            <Skeleton active paragraph={{ rows: 8 }} />
          </div>
        ) : error ? (
          <div className={styles.loading}>
            <Alert
              type="error"
              showIcon
              message="联系人详情加载失败"
              description={error.message}
              action={<Button onClick={onRefresh}>重试</Button>}
            />
          </div>
        ) : contact ? (
          <>
            <DetailSurface className={styles.heroCard}>
              <section className={styles.hero} data-crm-hero>
                <Avatar
                  size={64}
                  src={avatarUrl || undefined}
                  className={styles.heroAvatar}
                >
                  {displayName.slice(0, 1)}
                </Avatar>
                <div className={styles.heroMain}>
                  <div className={styles.heroTopRow} data-crm-hero-row>
                    <div>
                      <div className={styles.titleRow} data-crm-title-row>
                        <Title
                          level={3}
                          className={styles.title}
                          data-crm-title
                        >
                          {displayName}
                        </Title>
                        {contact?.isPrimaryDecisionMaker ? (
                          <Tag color="green">关键决策人</Tag>
                        ) : null}
                        {profile?.customer?.uuid ? (
                          <Tag color="processing">已关联客户</Tag>
                        ) : (
                          <Tag>未关联客户</Tag>
                        )}
                      </div>
                    </div>
                    <Space>
                      <Button
                        type="primary"
                        icon={<EditOutlined />}
                        onClick={() => setContactEditOpen(true)}
                      >
                        编辑
                      </Button>
                    </Space>
                  </div>
                  <div className={styles.metaGrid} data-crm-meta>
                    <span>职务 {contact?.post || '-'}</span>
                    <span>
                      最近联系 {formatCrmDate(contact?.contactLastTime, 'date')}
                    </span>
                    <span>
                      下次联系 {formatCrmDate(contact?.contactNextTime, 'date')}
                    </span>
                    <span>
                      负责人{' '}
                      {resolveRelationText({
                        value: contact?.ownerUserUuid,
                        directName:
                          contact?.ownerUserName ||
                          contact?.ownerName ||
                          contact?.ownerMemberName,
                        options: memberOptions,
                      })}
                    </span>
                  </div>
                </div>
              </section>
            </DetailSurface>

            <div className={styles.workspace} data-crm-workspace>
              <aside className={styles.navPanel} data-crm-nav>
                <DetailSurface
                  className={styles.navCard}
                  data-crm-nav-surface
                  title="工作视角"
                >
                  <DetailNavigation
                    className={styles.navMenu}
                    selectedKeys={displayedSection ? [displayedSection] : []}
                    items={sections.map((s) => ({
                      key: s.key,
                      label: (
                        <div className={styles.menuLabel}>
                          <span>{s.label}</span>
                          {'count' in s &&
                          typeof s.count === 'number' &&
                          s.count > 0 ? (
                            <Text type="secondary">{s.count}</Text>
                          ) : null}
                        </div>
                      ),
                    }))}
                    onClick={({ key }) =>
                      setActiveSection(key as ContactSectionKey)
                    }
                  />
                </DetailSurface>
              </aside>

              <main className={styles.mainPanel} data-crm-main>
                {!displayedSection && (
                  <Empty description="当前无可查看的分区" />
                )}
                <div className={styles.panelStack} data-crm-stack>
                  {displayedSection === 'details' && (
                    <DetailSurface
                      title={detailSectionLabel('details')}
                      className={styles.sectionCard}
                      extra={
                        <Button
                          type="link"
                          icon={<EditOutlined />}
                          onClick={() => setContactEditOpen(true)}
                        >
                          编辑
                        </Button>
                      }
                    >
                      <ProDescriptions<ContactItem>
                        dataSource={contact}
                        column={descColumns}
                      >
                        <ProDescriptions.Item
                          label="真实姓名"
                          dataIndex="name"
                        />
                        <ProDescriptions.Item label="标签" span={2}>
                          <EntityTagEditor
                            entityType="contact"
                            entityUuid={contact.uuid}
                            tags={contact.tags}
                            editable={
                              contact.recordPermission?.actions?.edit !== false
                            }
                          />
                        </ProDescriptions.Item>
                        <ProDescriptions.Item
                          label="称谓"
                          dataIndex="salutation"
                        />
                        <ProDescriptions.Item
                          label="客户"
                          dataIndex="customerUuid"
                          render={() => (
                            <CustomerReferenceLink
                              customerUuid={customerUuid}
                              customerName={customerName}
                            />
                          )}
                        />
                        <ProDescriptions.Item label="职务" dataIndex="post" />
                        <ProDescriptions.Item
                          label="性别"
                          render={() => sexLabel}
                        />
                        <ProDescriptions.Item
                          label="主要决策人"
                          render={(_, record) =>
                            record.isPrimaryDecisionMaker ? '是' : '否'
                          }
                        />
                        <ProDescriptions.Item
                          label="地区"
                          render={(_, record) =>
                            record.primaryAreaName ||
                            (Array.isArray(record.areaNames)
                              ? record.areaNames.join('、')
                              : '') ||
                            '-'
                          }
                        />
                        <ProDescriptions.Item
                          label="籍贯"
                          dataIndex="nativePlace"
                        />
                        <ProDescriptions.Item
                          label="详细地址"
                          dataIndex="detailAddress"
                        />
                        <ProDescriptions.Item
                          label="上级联系人"
                          render={(_, record) =>
                            resolveRelationLabel(
                              record.parentUuid,
                              parentOptions,
                              undefined,
                            )
                          }
                        />
                        <ProDescriptions.Item
                          label="备注"
                          dataIndex="remark"
                          span={2}
                        />
                      </ProDescriptions>
                    </DetailSurface>
                  )}

                  {displayedSection === 'activities' && (
                    <DetailSurface title="活动" className={styles.sectionCard}>
                      <ActivityPanel
                        bizType="contact"
                        bizUuid={contact.uuid}
                        bizName={contact.name}
                      />
                    </DetailSurface>
                  )}

                  {displayedSection === 'followUps' && (
                    <DetailSurface
                      title="跟进记录"
                      className={styles.sectionCard}
                    >
                      <ActivityPanelFollowUps
                        bizType="contact"
                        bizUuid={contact.uuid}
                        bizName={contact.name}
                        onRecordSaved={onRefresh}
                      />
                    </DetailSurface>
                  )}

                  {displayedSection === 'referrals' && (
                    <ReferralSection
                      key={contact.uuid}
                      contactUuid={contact.uuid}
                      onRefresh={onRefresh}
                      incoming={profile?.related?.referralsIn || EMPTY_PAGE}
                      outgoing={profile?.related?.referralsOut || EMPTY_PAGE}
                    />
                  )}

                  {displayedSection === 'teamMembers' && (
                    <TeamMembersPanel
                      bizType="contact"
                      bizUuid={contact.uuid}
                      viewMode={pageViewMode}
                      className={styles.sectionCard}
                    />
                  )}

                  {displayedSection === 'opportunities' && (
                    <DetailSurface
                      title={detailSectionLabel('opportunities')}
                      className={styles.sectionCard}
                      extra={
                        <Space size={4}>
                          <Button
                            type="link"
                            size="small"
                            onClick={() => setOpportunityCreateOpen(true)}
                          >
                            新建商机
                          </Button>
                          <Button
                            type="link"
                            size="small"
                            onClick={() =>
                              setContactOpportunityCreateOpen(true)
                            }
                          >
                            关联已有商机
                          </Button>
                        </Space>
                      }
                    >
                      <CrmList
                        dataSource={profile?.related.opportunities.list || []}
                        locale={{ emptyText: <QuietEmpty /> }}
                        renderItem={(item) => (
                          <CrmList.Item
                            actions={[
                              <Button
                                key="edit"
                                type="link"
                                onClick={() =>
                                  openRelatedEditor({
                                    kind: 'opportunity',
                                    record: item,
                                  })
                                }
                              >
                                编辑
                              </Button>,
                            ]}
                          >
                            <div
                              className={styles.metaItem}
                              style={{ width: '100%' }}
                            >
                              <span>
                                <OpportunityReferenceLink
                                  opportunityUuid={item.uuid}
                                  opportunityName={item.name || item.uuid}
                                />
                              </span>
                              <strong>
                                <Space wrap>
                                  {item.totalPrice ? (
                                    <Tag color="blue">
                                      金额 {item.totalPrice}
                                    </Tag>
                                  ) : null}
                                  {item.updateTime ? (
                                    <Tag>{formatCrmDate(item.updateTime)}</Tag>
                                  ) : null}
                                </Space>
                              </strong>
                            </div>
                          </CrmList.Item>
                        )}
                      />
                    </DetailSurface>
                  )}

                  {displayedSection === 'tasks' && (
                    <CrmTaskRelationPanel
                      title={detailSectionLabel('tasks')}
                      sourceSceneUuid="contact"
                      sourceObjectUuid={contact.uuid}
                      className={styles.sectionCard}
                    />
                  )}

                  {displayedSection === 'businessTrips' && (
                    <BusinessTripRelationPanel
                      title={detailSectionLabel('businessTrips')}
                      objectType="contact"
                      objectUuid={contact.uuid}
                      className={styles.sectionCard}
                    />
                  )}

                  {displayedSection === 'attachments' && (
                    <AttachmentPanel
                      bizType="contact"
                      bizUuid={contact.uuid}
                      emptyText="暂无附件"
                      onDataLoaded={(page) => setAttachmentCount(page.total)}
                    />
                  )}

                  {displayedSection === 'operationLogs' && (
                    <DetailOperationLogSection
                      bizType="contact"
                      bizUuid={contact.uuid}
                      viewMode={pageViewMode}
                      container="card"
                      cardProps={{ className: styles.sectionCard }}
                    />
                  )}
                </div>
              </main>

              <div className={styles.summaryPanel} data-crm-summary>
                <AiDetailPanel
                  bizType="contact"
                  bizUuid={contact?.uuid || ''}
                  initialSummary={profile.summaryPanel}
                  activeTab="summary"
                  contextTitle={contact?.name}
                />
              </div>
            </div>
          </>
        ) : null}
      </div>
      <ContactEditModal
        open={contactEditOpen}
        onOpenChange={setContactEditOpen}
        recordUuid={contact?.uuid}
        currentMemberUuid={undefined}
        onSuccess={() => {
          setContactEditOpen(false);
          onRefresh();
        }}
      />
      <ContactOpportunityCreateModal
        open={contactOpportunityCreateOpen}
        context={relationContext}
        onCancel={() => setContactOpportunityCreateOpen(false)}
        onSuccess={() => {
          setContactOpportunityCreateOpen(false);
          onRefresh();
        }}
      />
      <OpportunityEditModal
        open={opportunityCreateOpen}
        onOpenChange={setOpportunityCreateOpen}
        currentMemberUuid={undefined}
        initialValues={{
          customerUuid,
        }}
        onSuccess={() => {
          setOpportunityCreateOpen(false);
          onRefresh();
        }}
      />
      <Modal
        closable={{ 'aria-label': '关闭' }}
        keyboard
        open={Boolean(relatedEditor && relatedEditor.kind === 'opportunity')}
        title={
          relatedEditor
            ? relatedEditorTitleMap[relatedEditor.kind]
            : '编辑关联信息'
        }
        onCancel={closeRelatedEditor}
        onOk={() => void submitRelatedEditor()}
        confirmLoading={relatedSubmitting}
        destroyOnHidden
        width={720}
      >
        <Form
          form={relatedForm}
          layout="vertical"
          disabled={relatedSubmitting}
          preserve={false}
        >
          <Form.Item label="商机名称" name="name">
            <Input placeholder="请输入商机名称" />
          </Form.Item>
          <Form.Item label="预计成交时间" name="dealTime">
            <DatePicker
              style={{ width: '100%' }}
              showTime
              placeholder="请选择预计成交时间"
            />
          </Form.Item>
          <Form.Item label="总金额" name="totalPrice">
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              precision={2}
              placeholder="请输入总金额"
            />
          </Form.Item>
          <Form.Item label="结果状态" name="endStatus">
            <InputNumber
              style={{ width: '100%' }}
              placeholder="请输入结果状态编码"
            />
          </Form.Item>
          <Form.Item label="备注" name="remark">
            <Input.TextArea
              rows={4}
              showCount
              maxLength={500}
              placeholder="请输入备注"
            />
          </Form.Item>
        </Form>
      </Modal>
    </BusinessModal>
  );
}
